import { type FC, useContext, useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import styled, { createGlobalStyle } from 'styled-components'
import type { Swiper as SwiperClass } from 'swiper'
import { Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import premiumLock from 'assets/images/locked.png'
import avatarPlaceHolder from 'assets/images/user_placeholder.png'
import { IconPlayOutline, IconPostPauseOutline, IconPremiumPostLockedOutline} from 'assets/svg/sprite'
import * as spriteIcons from 'assets/svg/sprite'
import PrefetchedBlobImage from 'components/Media/PrefetchedBlobImage'
import { addToast } from 'components/Toast/Toast'
import AudioMessage from 'components/UI/AudioMessage/AudioMessage'
import UserDetails from 'components/UI/Modal/UserDetails/UserDetails'
import MessageReactions from 'components-ref/molecules/MessageReactions/MessageReactions'
import { MessageOptionsDropdown } from 'components-ref/molecules/post/OptionsDropdown/MessageOptionsDropdown'
import { StoryMedia } from 'components-ref/organisms'
import { useGlobalVideo } from 'context/globalVideoContext'
import { useModalContext } from 'context/modalContext'
import VideoSurface from 'context/VideoSurface'
import ProgressBar from 'features/VideoPlayer/ProgressBar/ProgressBar'
import ReplayGraphSVG from 'features/VideoPlayer/ReplayGraph'
import { useLongPress } from 'helpers/hooks/useLongPress'
import { useMessageVideoAnalytics } from 'helpers/hooks/useMessageVideoAnalytics/useMessageVideoAnalytics'
import { formatVideoTime, someTimeAgo, someTimeAgoMessages } from 'lib/dayjs'
import { useUserProfile } from 'store'
import colors from 'styles/colors'

import styles from './_message.module.scss'
import { InboxContext } from '../InboxContainer/InboxContext'
import FullscreenMessage from './components/FullscreenMessage/FullscreenMessage'
import GifLoader from './components/GifLoader/GifLoader'
import { LinkPost } from './components/LinkPost/LinkPost'
import MessageOptions from './components/MessageOptions/MessageOptions'
import MessageProcessing from './components/MessageProcessing/MessageProcessing'
import 'swiper/css'
import 'swiper/css/pagination'

const Message: FC<{
    scrollerEl?: HTMLElement | null
    message: any
    recieved: boolean
    sender: any
    reactions: any
    setReply: (val: any) => void
    addReaction: (type: string, reactionId: string) => void
    deleteMessage: (val: string) => void
    reportMessage: (val: string) => void
    purchaseMessage: (val: string) => void
    setMessageId?: (val: string | null) => void
    messageId?: string | null
    next_recieved?: boolean
    prev_recieved?: boolean
    seen?: boolean
    sending?: boolean
    showDate?: boolean
    welcomeMessage?: boolean
    otherUserData?: any
    isLastMessage?: boolean
    
}> = ({
    scrollerEl,
    message,
    recieved,
    sender,
    reactions,
    setReply,
    next_recieved,
    prev_recieved,
    seen,
    sending,
    addReaction,
    deleteMessage,
    purchaseMessage,
    reportMessage,
    showDate,
    setMessageId,
    messageId,
    welcomeMessage = false,
    otherUserData,
    isLastMessage
}) => {
    const modalData = useModalContext()
    const { t } = useTranslation()
    const [videoPlaying, setVideoPlaying] = useState<string[]>([])
    const [videoClocks, setVideoClocks] = useState<
        Record<
            string,
            {
                currentTime: number
                duration: number
                hasPlayed: boolean
            }
        >
    >({})
    const [previewOpen, setPreviewOpen] = useState(false)
    const {
        activeMediaId,
        setActiveMediaId,
        play,
        pause,
        stopLoading,
        seekTo,
        getVideoElement
    } = useGlobalVideo()
    const inboxContext = useContext(InboxContext)
    const userData = useUserProfile()
    const navigate = useNavigate()

    const appUrl = process.env.REACT_APP_URL
    const linkRegex = new RegExp(`${appUrl}/post/\\d+`)

    const [useAltTime, setUseAltTime] = useState(false)
    const toggleTime = () => setUseAltTime(prev => !prev)

    const [activeIndex, setActiveIndex] = useState(0)

    const swiperRef = useRef<any>(null)
    const anyVideoPlaying = videoPlaying.length > 0
    const messageContainerRef = useRef<HTMLDivElement | null>(null)
    const timestampRef = useRef<HTMLDivElement | null>(null)

    const location = useLocation()
    const isWelcomeMessageRoute = location.pathname.includes(
        '/settings/general/chat-settings/welcome-message'
    )

    const hasPrice = Number(message?.price ?? 0) > 0
    const isSender = Boolean(message?.is_sender)
    const isCreator = userData?.role === 'model'
    const isPurchased = Boolean(message?.is_purchased)

    const showPriceBadge = hasPrice && isCreator && isSender
    const showPremiumBadge = hasPrice && !isSender && isPurchased


    const [messageOptionsOpen, setMessageOptionsOpen] = useState(false)

    const openMessageOptions = () => {
        if (sending ?? welcomeMessage) return
        setMessageOptionsOpen(true)
    }

    const longPressHandlers = useLongPress({
        delay: 500,
        moveThreshold: 20,
        onLongPress: openMessageOptions,
    })


    // Who is the owner/creator of this message?
    const isOwner = Boolean(message?.is_sender)

    // Locked (for THIS viewer) means: paywalled and not purchased and not the owner
    const isLockedForViewer =
        !isOwner &&
        Number(message?.price ?? 0) > 0 &&
        !message?.is_purchased

    const formattedTime = useAltTime
        ? someTimeAgo(message.created_at)
        : someTimeAgoMessages(message.created_at)

    // merge media
    const hasClientMediaPreview =
        Array.isArray(message.__client_media_preview) &&
        message.__client_media_preview.length > 0


   const mediaArray: any = hasClientMediaPreview
    ? [message.__client_media_preview]
    : [
        message.photos || [],
        message.videos || [],
        message.sounds || [],
        message.photos_preview || [],
        message.videos_preview || []
    ]
    const tmpmergedMediaArray: any = [].concat
        .apply([], mediaArray)
        .sort((a: any, b: any) => a.order - b.order)
    const mediaIds = new Set<string>()

    const mergedMediaArray = tmpmergedMediaArray.filter((media: any) => {
        const mediaId = String(media.id ?? '')

        if (!mediaId || mediaIds.has(mediaId)) {
            return false
        }

        mediaIds.add(mediaId)

        return true
    })

    const activeMedia = mergedMediaArray[activeIndex]


    useEffect(() => {
        if (!activeMediaId || previewOpen) return

        const item = mergedMediaArray.find(
            (media: any) =>
                media?.type === 'video' &&
                String(media?.id) === String(activeMediaId)
        )

        if (!item) return

        const mediaId = String(item.id)

        const url =
            item.src ||
            item.url ||
            item.path ||
            ''

        if (!url) return

        let video: HTMLVideoElement | null = null

        const syncClock = () => {
            if (!video) return

            const currentTime =
                Number.isFinite(video.currentTime)
                    ? video.currentTime
                    : 0

            const duration =
                Number.isFinite(video.duration) &&
                video.duration > 0
                    ? video.duration
                    : Number(item.duration ?? 0)

            setVideoClocks(previous => ({
                ...previous,
                [mediaId]: {
                    currentTime,
                    duration,
                    hasPlayed:
                        previous[mediaId]?.hasPlayed ||
                        !video!.paused ||
                        currentTime > 0
                }
            }))
        }

        const handlePlay = () => {
            if (!video) return

            setVideoClocks(previous => ({
                ...previous,
                [mediaId]: {
                    currentTime:
                        Number.isFinite(video!.currentTime)
                            ? video!.currentTime
                            : 0,

                    duration:
                        Number.isFinite(video!.duration) &&
                        video!.duration > 0
                            ? video!.duration
                            : Number(item.duration ?? 0),

                    hasPlayed: true
                }
            }))
        }

        const frame = requestAnimationFrame(() => {
            video = getVideoElement(
                mediaId,
                url
            )

            if (!video) return

            syncClock()

            video.addEventListener(
                'loadedmetadata',
                syncClock
            )

            video.addEventListener(
                'durationchange',
                syncClock
            )

            video.addEventListener(
                'timeupdate',
                syncClock
            )

            video.addEventListener(
                'play',
                handlePlay
            )
        })

        return () => {
            cancelAnimationFrame(frame)

            if (!video) return

            video.removeEventListener(
                'loadedmetadata',
                syncClock
            )

            video.removeEventListener(
                'durationchange',
                syncClock
            )

            video.removeEventListener(
                'timeupdate',
                syncClock
            )

            video.removeEventListener(
                'play',
                handlePlay
            )
        }
    }, [
        activeMediaId,
        previewOpen,
        getVideoElement
    ])

    const hasPortraitMedia = mergedMediaArray.some(
        (media: any) => {
            const orientation =
                String(
                    media?.orientation ??
                    media?.preview?.orientation ??
                    ''
                ).toLowerCase()

            const isVisualMedia =
                media?.type === 'video' ||
                media?.type === 'photo' ||
                (
                    media?.preview &&
                    typeof media.preview === 'object'
                )

            return (
                isVisualMedia &&
                orientation === 'portrait'
            )
        }
    )

    const renderTextWithLinks = (text: string) => {
    const parts = text.split(
        /(\bhttps?:\/\/[^\s]+|\bwww\.[^\s]+|\b[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}[^\s]*)/g
    )

    return parts.map((part, i) => {
        const isLink = /^(https?:\/\/|www\.|[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/.test(part)

        if (!isLink) return <span key={i}>{part}</span>

        const url = part.startsWith('http') ? part : `https://${part}`

        if (!/^https?:\/\//i.test(url)) {
        return <span key={i}>{part}</span>
        }

        return (
        <a
            key={i}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
        >
            {part}
        </a>
        )
    })
    }


    // width for graph
    const widthRef = useRef<HTMLDivElement | null>(null)
    const [graphWidth, setGraphWidth] = useState<number>(0)
    useEffect(() => {
        const el = widthRef.current
        if (!el) return
        const ro = new ResizeObserver(([entry]) =>
            setGraphWidth(entry.contentRect.width)
        )
        ro.observe(el)
        return () => ro.disconnect()
    }, [])

    const {
        duration,
        progressData,
        currentSeek,
        showReplayGraph,
        hasGraphData,
        mostViewedSegments,
        toggleReplayGraph,
        hlsProps,
        onSeek,
        getVideoElement: getAnalyticsVideoElement
    } = useMessageVideoAnalytics({
        viewerUserId: userData?.id
    })

    const shouldHideBadges = showReplayGraph && hasGraphData

    useEffect(() => {
        if (previewOpen) {
            if (inboxContext) {
                inboxContext.changeClass(styles.inbox_fullscreen_open, 'add')
            }
        } else {
            if (inboxContext) {
                inboxContext.changeClass(styles.inbox_fullscreen_open, 'remove')
            }
        }
    }, [previewOpen, inboxContext])

    const toggleVideo = (id: string, url: string) => {
        const mediaId = String(id)

        if (videoPlaying.includes(mediaId)) {
            pause()
            return
        }

        const video = getVideoElement(mediaId, url)

        if (
            video &&
            (
                video.ended ||
                (
                    Number.isFinite(video.duration) &&
                    video.duration > 0 &&
                    video.currentTime >= video.duration - 0.1
                )
            )
        ) {
            video.currentTime = 0
        }

        setActiveMediaId(mediaId)

        void play(mediaId, url)
    }

    const checkLink = (message: string) => {
        return (
            linkRegex.test(message) &&
            !/\S/.test(message.replace(linkRegex, ''))
        )
    }

    const renderReply = () => {
        return (
            message.replied_message?.message && (
                <div
                    className={`${styles.replied_message_text} ${
                        message.replied_message?.message.body === ''
                            ? styles.replied_message_audio
                            : ''
                    }`}
                >
                    {recieved && (
                        <div className={styles.replied_message_response}>
                            <spriteIcons.IconChatBUbblesOutline />
                            Response
                        </div>
                    )}
                    <spriteIcons.IconQuotes />
                    {message.replied_message?.message?.body !== '' ? (
                        <p>{message.replied_message.message.body}</p>
                    ) : (
                        message.replied_message?.message.sounds.length > 0 && (
                            <AudioMessage
                                audioReady={true}
                                waveBackgroundColor="transparent"
                                waveHeight={30}
                                timerTextSize={12}
                                timerTextColor={recieved ? 'gray' : 'white'}
                                waveColor={recieved ? 'gray' : 'white'}
                                audioBlob={
                                    message.replied_message?.message.sounds[0]
                                        .src ||
                                    message.replied_message?.message.sounds[0]
                                        .url ||
                                    message.replied_message?.message.sounds[0]
                                        .path ||
                                    ''
                                }
                                customClass={`${styles.message_sound_player} ${
                                    recieved
                                        ? styles.message_sound_player_recieved
                                        : ''
                                }`}
                            />
                        )
                    )}
                </div>
            )
        )
    }

    const renderStoryReply = () => {
        return (
            message.type === 'story' && (
                <>
                    <Text>{t('youRepliedToStory')}</Text>
                    {message.story !== null ? (
                        <StoryContainer>
                            <Link
                                to={'/view/story'}
                                state={{
                                    fromProfile: true,
                                    storyPost: {
                                        stories: [message.story],
                                        user: message.story.user
                                    }
                                }}
                            >
                                <StoryMedia
                                    user={message.story.user}
                                    story={message.story}
                                    isPlayerMuted={true}
                                    setIsPaused={() => null}
                                    setAction={() => null}
                                    fullScreen={false}
                                    isPaused={true}
                                />
                            </Link>
                        </StoryContainer>
                    ) : (
                        ''
                    )}
                </>
            )
        )
    }

    const renderPollReply = () => {
        return (
            message.type === 'poll' && (
                <PollContainer>
                    <PollTitle>
                        <spriteIcons.IconChatBUbblesOutline
                            width="16"
                            height="16"
                        />
                        {t('response').toUpperCase()}
                    </PollTitle>
                    <PollQuestion> {message.poll.question}</PollQuestion>
                </PollContainer>
            )
        )
    }

    const renderMediaNew = () => {
        if (mergedMediaArray.length === 0) return null

        // consider both persisted and optimistic media
    const isAnyVideoProcessing =
        isOwner &&
        (
            (
                Array.isArray(message?.videos) &&
                message.videos.some(
                    (v: { status?: string }) =>
                        v?.status === 'processing'
                )
            ) ||
            mergedMediaArray.some(
                (m: { type?: string; status?: string }) =>
                    m?.type === 'video' &&
                    m?.status === 'processing'
            )
        )

        // counts for overlays / badges
        const finishedVideoCount =
            message?.videos?.filter((v: any) => {
                const videoUrl =
                    v?.src ||
                    v?.url ||
                    v?.path ||
                    ''

                return (
                    Boolean(videoUrl) &&
                    v?.status !== 'processing'
                )
            })?.length ?? 0
        const previewVideoCount = message?.videos_preview?.length ?? 0
        const displayVideoCount = finishedVideoCount || previewVideoCount

        const photoCount = message?.photos?.length ?? 0
        const previewPhotoCount = message?.photos_preview?.length ?? 0
        const displayPhotoCount = photoCount || previewPhotoCount

        const totalDisplayMediaCount = displayVideoCount + displayPhotoCount

        const isFree = message?.price == null || Number(message?.price) === 0
        const compactOverlay = totalDisplayMediaCount <= 1 && isFree
        const hasThumbPreview = mergedMediaArray.some(
            (media: any) =>
                media.preview?.type === 'thumb' &&
                Boolean(media.preview?.src)
        )

        const hasPlayablePreview = mergedMediaArray.some(
            (media: any) =>
                media.preview?.type === 'clip' ||
                media.preview?.type === 'trimmed_video'
        )
        
        if (isAnyVideoProcessing) {

            return (
                <MessageProcessing
                    recieved={recieved}
                    portrait={hasPortraitMedia}
                />
            )
        }

        return (
                <div
                    className={`${styles.message_swiper_wrapper} ${
                        recieved
                            ? styles.message_swiper_wrapper_recieved
                            : styles.message_swiper_wrapper_sent
                    }`}
                    {...longPressHandlers}
                >
                <div className={`${styles.message_media_bubble} ${
                    hasPortraitMedia
                        ? styles.message_media_bubble_portrait
                        : ''
                }`}>
                    <div className={styles.message_media_clip}>
                        <PaginationBullet />
                            <Swiper
                            direction="horizontal"
                            spaceBetween={20}
                            slidesPerView={1}
                            slidesPerGroup={1}
                            ref={swiperRef}
                            className={`${styles.message_swiper} ${
                                hasPortraitMedia
                                    ? styles.message_swiper_portrait
                                    : ''
                            } ${
                                isLockedForViewer && !anyVideoPlaying
                                    ? 'message-swiper--locked'
                                    : ''
                            }`}
                            onSlideChange={(swiper: SwiperClass) => {
                                setActiveIndex(swiper.activeIndex)
                            }}
                            longSwipesRatio={0.1}
                            pagination={anyVideoPlaying ? false : { clickable: true }}
                            mousewheel={true}
                            resistanceRatio={0}
                            modules={[Pagination]}
                            >
                        {mergedMediaArray.map((item: any, key: number) => {
                            const itemOrientation =
                                String(
                                    item?.orientation ??
                                    item?.preview?.orientation ??
                                    ''
                                ).toLowerCase()

                            const itemIsPortrait =
                                itemOrientation === 'portrait'

                            const isLandscapeInPortraitCarousel =
                                hasPortraitMedia &&
                                itemOrientation === 'landscape'

                            // --------------------
                            // PHOTOS
                            // --------------------
                            if (item.type === 'photo') {

                            const rawSrc =
                                item.previewUrl ||
                                item.src ||
                                item.thumb_url ||
                                item.url ||
                                item.path ||
                                (isOwner ? item?.preview?.src : '') ||
                                ''

                                  return rawSrc ? (
                                    <SwiperSlide className={styles.message_slide} key={key}>
                                      <div
                                        className={`${styles.message_photo} ${
                                            String(item?.orientation ?? '').toLowerCase() === 'portrait'
                                                ? styles.message_photo_portrait
                                                : ''
                                        }`}
                                        onClick={() => {
                                          if (isLockedForViewer) return
                                          setPreviewOpen(true)
                                        }}
                                      >
                                        {isLandscapeInPortraitCarousel && (
                                            <img
                                                src={rawSrc}
                                                alt=""
                                                aria-hidden="true"
                                                className={styles.message_mixed_blur}
                                            />
                                        )}
                                        <div className={isLockedForViewer ? styles.previewonImg : ''}>
                                          <PrefetchedBlobImage
                                            url={rawSrc}
                                            prefetchUrls={[item.url]}
                                            rootMargin="400px 0px 400px 0px"
                                            alt="message"
                                            disabled={isLockedForViewer}
                                            placeholder={<div className={styles.media_skeleton} />}
                                            className={`${styles.prefetch_wrapper} ${
                                                isLandscapeInPortraitCarousel
                                                    ? styles.message_mixed_foreground
                                                    : ''
                                            }`}
                                            imgClassName={`${styles.prefetch_img} ${
                                                isLandscapeInPortraitCarousel
                                                    ? styles.message_mixed_contain
                                                    : ''
                                            }`}
                                            root={scrollerEl}
                                          />
                                        </div>
                                      </div>
                                    </SwiperSlide>
                                    ) : (
                                        <SwiperSlide key={key}>
                                            {isLockedForViewer ? (
                                                <Placeholder>
                                                    <PlaceholderBackground />
                                                    <spriteIcons.IconLocked
                                                        color="#fff"
                                                        width="50"
                                                        height="50"
                                                    />
                                                    <button
                                                        className={styles.message_buy_button}
                                                        onClick={() => {
                                                            purchaseMessage(message.id)
                                                        }}
                                                    >
                                                        {t('buy')} ${message.price}
                                                    </button>
                                                </Placeholder>
                                            ) : (
                                                <MessageProcessing
                                                recieved={recieved}
                                                portrait={hasPortraitMedia}
                                                />
                                            )}
                                        </SwiperSlide>
                                    )
                                }

                                // --------------------
                                // FULL VIDEOS
                                // --------------------
                            if (item.type === 'video') {
                                const videoUrl =
                                    item.src ||
                                    item.url ||
                                    item.path ||
                                    ''

                                const isProcessing =
                                    item?.status === 'processing'

                                const isVideoReady =
                                    Boolean(videoUrl) &&
                                    !isProcessing

                                if (isVideoReady) {
                                    const isPlaying = videoPlaying.includes(item.id)

                                    const ownerId =
                                        item.user_id ??
                                        message?.user_id ??
                                        message?.user?.id ??
                                        sender?.id ??
                                        otherUserData?.id

                                    const posterUrl = item.active_thumb || ''

                                    return (
                                    <SwiperSlide
                                        className={`${styles.message_video} ${
                                            String(item?.orientation ?? '').toLowerCase() === 'portrait'
                                                ? styles.message_video_portrait
                                                : ''
                                        } ${
                                            isLandscapeInPortraitCarousel
                                                ? styles.message_video_mixed_landscape
                                                : ''
                                        }`}
                                        key={key}
                                        onClick={() => {
                                            if (isLockedForViewer) {
                                                if (
                                                    item.locked === 1 &&
                                                    message.price > 0 &&
                                                    !message.is_purchased
                                                ) {
                                                    if (
                                                        userData?.status === 'restricted' &&
                                                        userData?.role === 'fan'
                                                    ) {
                                                        addToast('error', 'Your account is restricted.')
                                                        return
                                                    }

                                                    purchaseMessage(message.id)
                                                }

                                                return
                                            }

                                            setActiveMediaId(String(item.id))
                                            setPreviewOpen(true)
                                        }}
                                    >
                                        <div
                                        ref={widthRef}
                                        className={`${styles.video_wrapper} message-video-context`}
                                        >
                                        {/* Prefetch ONLY the poster thumb, same IO/rootMargin flow as photos */}
                                        {!!posterUrl && (
                                            <PrefetchedBlobImage
                                            url={posterUrl}
                                            rootMargin="400px 0px 400px 0px"
                                            disabled={isLockedForViewer}
                                            placeholder={null}
                                            className={styles.videoPosterPrefetcher}
                                            root={scrollerEl}
                                            alt=""
                                            />
                                        )}

                                        {!!message.price && userData?.role === 'model' && !isPlaying && (
                                            <div
                                            className={styles.previewBadge}
                                            onClick={e => e.stopPropagation()}
                                            >
                                            <div className={styles.info_top_left}>
                                                {item.preview === false ? 'Preview Off' : 'Preview On'}
                                            </div>
                                            </div>
                                        )}
                                        {isLandscapeInPortraitCarousel && !!posterUrl && (
                                            <img
                                                src={posterUrl}
                                                alt=""
                                                aria-hidden="true"
                                                className={styles.message_mixed_blur}
                                            />
                                        )}
                                        <VideoSurface
                                            ownerType='message'
                                            ownerId={`${message.id}-${item.id}`}
                                            mediaId={String(item.id)}
                                            videoId={String(item.id)}
                                            videoUserId={ownerId}
                                            url={item.src || item.url || item.path || ''}
                                            cloudfront={item.cloudfront}
                                            poster={posterUrl}
                                            active={activeMediaId === String(item.id) && !previewOpen}
                                            onPlay={() => {
                                                setVideoPlaying([String(item.id)])
                                            }}
                                            onPause={() => {
                                                setVideoPlaying(previous =>
                                                    previous.filter(
                                                        id => id !== String(item.id)
                                                    )
                                                )
                                            }}
                                            onEnded={() => {
                                                setVideoPlaying(previous =>
                                                    previous.filter(
                                                        id => id !== String(item.id)
                                                    )
                                                )
                                            }}
                                            className={`postVideo__videoSurface ${
                                                isLandscapeInPortraitCarousel
                                                    ? styles.message_video_mixed_contain
                                                    : ''
                                            }`}
                                        />

                                        {!previewOpen && (
                                            <div
                                                className={styles.message_video_icons}
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    toggleVideo(
                                            item.id,
                                            item.src || item.url || item.path || ''
                                        )
                                                }}
                                            >
                                                {isPlaying ? (
                                                    <span className={styles.message_pause_icon}>
                                                        <IconPostPauseOutline />
                                                    </span>
                                                ) : (
                                                    <IconPlayOutline color='#ffffff' />
                                                )}
                                            </div>
                                        )}
                                        <div
                                            style={{ zIndex: 9 }}
                                            className="post__files__file__duration"
                                        >
                                            {(() => {
                                                const clock =
                                                    videoClocks[String(item.id)]

                                                const totalDuration =
                                                    clock?.duration ||
                                                    Number(item.duration ?? 0)

                                                if (clock?.hasPlayed) {
                                                    return `${formatVideoTime(
                                                        clock.currentTime
                                                    )} / ${formatVideoTime(
                                                        totalDuration
                                                    )}`
                                                }

                                                return formatVideoTime(
                                                    totalDuration
                                                )
                                            })()}
                                        </div>

                                        <ProgressBarWrapper>
                                            <Progress>
                                                <ProgressBar
                                                    getVideoElement={() =>
                                                        getVideoElement(
                                                            String(item.id),
                                                            item.src || item.url || item.path || ''
                                                        )
                                                    }
                                                    onSeek={fraction => {
                                                        if (activeMediaId !== String(item.id)) return
                                                        seekTo(fraction, 'fraction')
                                                    }}
                                                />
                                            </Progress>
                                        </ProgressBarWrapper>

                                        </div>
                                    </SwiperSlide>
                                    )
                                }
                                if (
                                    item?.status === 'processing' &&
                                    isOwner
                                ) {
                                    return (
                                        <SwiperSlide key={key}>
                                            <MessageProcessing
                                            recieved={recieved}
                                            portrait={hasPortraitMedia}
                                            />
                                        </SwiperSlide>
                                    )
                                }
                                }

                                // --------------------
                                // PREVIEW OBJECTS (thumb / clip / trimmed)
                                // --------------------
                                if (
                                    item.preview &&
                                    typeof item.preview === 'object'
                                ) {

                                if (
                                message.photos_preview?.some(
                                    (photo: any) => String(photo.id) === String(item.id)
                                )
                                ) {
                                return (
                                    <SwiperSlide
                                    key={key}
                                    className={styles.message_slide}
                                    >
                                    <div className={styles.message_photo}>
                                        {item.preview?.src ? (
                                        <img src={item.preview.src} alt="Photo preview" />
                                        ) : (
                                        <div className={styles.locked_media_background} />
                                        )}
                                    </div>
                                    </SwiperSlide>
                                )
                                }

                                    // VIDEO THUMB PREVIEW
                                if (
                                item.preview.type === 'thumb' &&
                                message.videos_preview?.some(
                                    (video: any) => String(video.id) === String(item.id)
                                )
                                ) {
                                    return item.preview.src ? (
                                            <SwiperSlide
                                            className={`${styles.message_video} ${
                                                itemIsPortrait
                                                ? styles.message_video_portrait
                                                : ''
                                            } ${
                                                isLandscapeInPortraitCarousel
                                                ? styles.message_video_mixed_landscape
                                                : ''
                                            }`}
                                            key={key}
                                            onClick={() => {
                                                if (!item.preview?.src) return
                                                setPreviewOpen(true)
                                            }}
                                        >
                                            <div
                                            className={`${styles.video_wrapper} message-video-context`}
                                            >
                                            {isLandscapeInPortraitCarousel && (
                                                <img
                                                src={item.active_thumb || item.preview.src}
                                                alt=""
                                                aria-hidden="true"
                                                className={styles.message_mixed_blur}
                                                />
                                            )}

                                            <img
                                                src={item.preview.src}
                                                alt="video preview"
                                                className={
                                                isLandscapeInPortraitCarousel
                                                    ? styles.message_mixed_preview_foreground
                                                    : styles.message_video_preview_image
                                                }
                                            />

                                                {typeof item.duration === 'number' &&
                                                    item.duration > 0 && (
                                                    <div
                                                        style={{ zIndex: 9 }}
                                                        className="post__files__file__duration"
                                                    >
                                                        {(() => {
                                                            const clock =
                                                                videoClocks[String(item.id)]

                                                            const totalDuration =
                                                                clock?.duration ||
                                                                Number(item.duration ?? 0)

                                                            if (clock?.hasPlayed) {
                                                                return `${formatVideoTime(
                                                                    clock.currentTime
                                                                )} / ${formatVideoTime(
                                                                    totalDuration
                                                                )}`
                                                            }

                                                            return formatVideoTime(
                                                                totalDuration
                                                            )
                                                        })()}
                                                    </div>
                                                    )}
                                            </div>
                                        </SwiperSlide>
                                    ) : isLockedForViewer ? (
                                        <SwiperSlide
                                            className={styles.message_video}
                                            key={key}
                                        >
                                            <div className={styles.locked_media_background} />
                                        </SwiperSlide>
                                    ) : (
                                        <SwiperSlide key={key}>
                                            <MessageProcessing
                                            recieved={recieved}
                                            portrait={hasPortraitMedia}
                                            />
                                        </SwiperSlide>
                                    )
                                }

                                    // CLIP / TRIMMED VIDEO PREVIEW (playable)
                                    if (
                                        item.preview.type === 'clip' ||
                                        item.preview.type === 'trimmed_video'
                                    ) {
                                        const ownerId =
                                            item.user_id ??
                                            message?.user_id ??
                                            message?.user?.id ??
                                            sender?.id ??
                                            otherUserData?.id

                                        return item.preview.src ? (
                                                <SwiperSlide
                                                className={`${styles.message_video} ${
                                                    itemIsPortrait
                                                    ? styles.message_video_portrait
                                                    : ''
                                                } ${
                                                    isLandscapeInPortraitCarousel
                                                    ? styles.message_video_mixed_landscape
                                                    : ''
                                                }`}
                                                key={key}
                                                onClick={() => {
                                                    if (
                                                        item.locked === 1 &&
                                                        message.price > 0 &&
                                                        !message.is_purchased
                                                    ) {
                                                        purchaseMessage(message.id)
                                                    } else {
                                                        setActiveMediaId(String(item.id))
                                                        setPreviewOpen(true)
                                                    }
                                                }}
                                            >
                                                <div
                                                    className={`${styles.video_wrapper} message-video-context`}
                                                >
                                                    {isLandscapeInPortraitCarousel && item.active_thumb && (
                                                    <img
                                                        src={item.active_thumb}
                                                        alt=""
                                                        aria-hidden="true"
                                                        className={styles.message_mixed_blur}
                                                    />
                                                    )}
                                                    <VideoSurface
                                                        ownerType='message'
                                                        ownerId={`${message.id}-${item.id}`}
                                                        mediaId={String(item.id)}
                                                        videoId={String(item.id)}
                                                        videoUserId={ownerId}
                                                        url={item.preview.src}
                                                        cloudfront={item.preview?.cloudfront ?? item.preview_cloudfront ?? null}
                                                        poster={item.active_thumb}
                                                        active={
                                                            activeMediaId === String(item.id) &&
                                                            !previewOpen
                                                        }
                                                        onPlay={() => {
                                                            setVideoPlaying([String(item.id)])
                                                        }}
                                                        onPause={() => {
                                                            setVideoPlaying(previous =>
                                                                previous.filter(
                                                                    id => id !== String(item.id)
                                                                )
                                                            )
                                                        }}
                                                        onEnded={() => {
                                                            setVideoPlaying(previous =>
                                                                previous.filter(
                                                                    id => id !== String(item.id)
                                                                )
                                                            )
                                                        }}
                                                        className={`postVideo__videoSurface ${
                                                            isLandscapeInPortraitCarousel
                                                                ? styles.message_video_mixed_contain
                                                                : ''
                                                        }`}
                                                    />

                                                <div
                                                    style={{ zIndex: 9 }}
                                                    className="post__files__file__duration"
                                                >
                                                    {(() => {
                                                        const clock =
                                                            videoClocks[String(item.id)]

                                                        const totalDuration =
                                                            clock?.duration ||
                                                            Number(item.duration ?? 0)

                                                        if (clock?.hasPlayed) {
                                                            return `${formatVideoTime(
                                                                clock.currentTime
                                                            )} / ${formatVideoTime(
                                                                totalDuration
                                                            )}`
                                                        }

                                                        return formatVideoTime(
                                                            totalDuration
                                                        )
                                                    })()}
                                                </div>

                                                    {userData?.id === ownerId && (
                                                        <MostPlayed>
                                                            <GraphButton
                                                                onClick={e => {
                                                                    e.stopPropagation()
                                                                    toggleReplayGraph(
                                                                        Array.isArray(
                                                                            item?.aggregated_segment_data
                                                                        )
                                                                            ? item.aggregated_segment_data
                                                                            : []
                                                                    )
                                                                }}
                                                            >
                                                                <>
                                                                    <spriteIcons.IconReplayGraphRounded
                                                                        color="#fff"
                                                                    />
                                                                    <span>Most Replayed</span>
                                                                </>
                                                            </GraphButton>

                                                            {showReplayGraph && (
                                                                <GraphBar>
                                                                    <ReplayGraphSVG
                                                                        data={mostViewedSegments}
                                                                        height={60}
                                                                        // width={graphWidth}
                                                                    />
                                                                </GraphBar>
                                                            )}
                                                        </MostPlayed>
                                                    )}

                                                    <ProgressBarWrapper>
                                                        <Progress>
                                                            <ProgressBar
                                                                getVideoElement={() =>
                                                                    getVideoElement(
                                                                        String(item.id),
                                                                        item.preview.src
                                                                    )
                                                                }
                                                                onSeek={fraction => {
                                                                    if (activeMediaId !== String(item.id)) return
                                                                    seekTo(fraction, 'fraction')
                                                                }}
                                                            />
                                                        </Progress>
                                                    </ProgressBarWrapper>

                                                    {item.preview === 0 &&
                                                        !message.is_purchased && (
                                                            <div className={styles.lock_overlay}>
                                                                <spriteIcons.IconPremiumPostLockedOutline />
                                                                {/* <p>Premium</p> */}
                                                            </div>
                                                        )}
                                                </div>

                                                {(item.preview !== 0 ||
                                                    message.is_purchased) && (
                                                        <div
                                                            className={`${styles.message_video_icons} ${
                                                                isLockedForViewer && hasPlayablePreview
                                                                    ? styles.message_video_icons_with_buy
                                                                    : ''
                                                            }`}
                                                            onClick={e => {
                                                                e.stopPropagation()
                                                                toggleVideo(
                                                                    item.id,
                                                                    item.preview.src
                                                                )
                                                            }}
                                                        >
                                                        {videoPlaying.includes(item.id) ? (
                                                            <span className={styles.message_pause_icon}>
                                                                <IconPostPauseOutline />
                                                            </span>
                                                        ) : (
                                                            <IconPlayOutline color="#ffffff" />
                                                        )}
                                                    </div>
                                                )}
                                            </SwiperSlide>
                                        ) : (
                                            <SwiperSlide key={key}>
                                                <MessageProcessing
                                                recieved={recieved}
                                                portrait={hasPortraitMedia}
                                                />
                                            </SwiperSlide>
                                        )
                                    }

                                    // generic image preview
                                    return item.preview.src ? (
                                        <SwiperSlide
                                            className={`${styles.message_photo} ${
                                                String(item?.orientation ?? '').toLowerCase() === 'portrait'
                                                    ? styles.message_photo_portrait
                                                    : ''
                                            }`}
                                            key={key}
                                            onClick={() => {
                                                setPreviewOpen(true)
                                            }}
                                        >
                                            <img
                                                src={item.preview.src || ''}
                                                alt="preview"
                                            />
                                            {item.locked === 1 &&
                                                message.price > 0 &&
                                                !message.is_purchased && (
                                                    <button
                                                        className={styles.message_buy_button}
                                                    >
                                                        {t('buy')} ${message.price}
                                                    </button>
                                                )}
                                        </SwiperSlide>
                                    ) : (
                                        <SwiperSlide key={key}>
                                            <MessageProcessing
                                            recieved={recieved}
                                            portrait={hasPortraitMedia}
                                            />
                                        </SwiperSlide>
                                    )
                                }

                                // --------------------
                                // SOUNDS
                                // --------------------
                                if (item.type === 'sound') {
                                    return (
                                        <SwiperSlide
                                            className={styles.message_sound_wrapper}
                                            key={key}
                                        >
                                            {!message.replied_message && !sending && (
                                                <MessageOptions
                                                    message={message}
                                                    recieved={recieved}
                                                    addReaction={addReaction}
                                                    hideOptionsForMedia={true}
                                                />
                                            )}
                                            <div className={styles.message_sound}>
                                                {renderReply()}
                                                <MessageReactions
                                                    reactions={reactions}
                                                    containerClassName={
                                                        styles.message_reactions_container
                                                    }
                                                    reactionClassName={styles.message_reaction}
                                                />
                                                <AudioMessage
                                                    audioReady={true}
                                                    waveBackgroundColor="transparent"
                                                    waveHeight={30}
                                                    timerTextSize={12}
                                                    timerTextColor={recieved ? 'gray' : 'white'}
                                                    waveColor={recieved ? 'gray' : 'white'}
                                                    audioBlob={
                                                        item.src || item.url || item.path || ''
                                                    }
                                                    customClass={`${
                                                        styles.message_sound_player
                                                    } ${
                                                        recieved
                                                            ? styles.message_sound_player_recieved
                                                            : ''
                                                    }`}
                                                />
                                            </div>
                                        </SwiperSlide>
                                    )
                                }

                                return null
                            })}

                            <>
                                {isLockedForViewer && !anyVideoPlaying && (
                                    <>
                                    <div
                                        className={`${styles.message_locked_above_buy} ${
                                        hasThumbPreview || hasPlayablePreview
                                            ? styles.message_locked_above_buy_thumb
                                            : ''
                                        } ${
                                        activeMedia &&
                                        message.photos_preview?.some(
                                            (photo: any) => String(photo.id) === String(activeMedia.id)
                                        ) &&
                                        !activeMedia.preview?.src
                                            ? styles.message_locked_above_buy_photo
                                            : ''
                                        } ${
                                        activeMedia?.preview?.type === 'thumb' &&
                                        Boolean(activeMedia?.preview?.src) &&
                                        activeMedia?.preview?.type === 'thumb' &&
                                        Boolean(activeMedia?.preview?.src) &&
                                        hasPortraitMedia
                                        ? styles.message_locked_above_buy_portrait_preview
                                        : ''
                                        }`}
                                    >
                                        {!hasPlayablePreview &&
                                            (hasThumbPreview ? (
                                                <IconPremiumPostLockedOutline />
                                            ) : (
                                                <img
                                                    src={premiumLock}
                                                    alt=""
                                                    className={styles.message_locked_image}
                                                />
                                            ))}

{/*                                         <p
                                            className={`${styles.message_locked_text} ${
                                                hasThumbPreview || hasPlayablePreview
                                                    ? styles.message_locked_text_thumb
                                                    : ''
                                            }`}
                                        >
                                            Premium
                                        </p> */}
                                    </div>

                                        <button
                                            className={styles.message_buy_button}
                                            onClick={() => {
                                                if (
                                                    userData?.status === 'restricted' &&
                                                    userData?.role === 'fan'
                                                ) {
                                                    addToast('error', 'Your account is restricted.')
                                                    return
                                                }

                                                purchaseMessage(message.id)
                                            }}
                                        >
                                            {t('buy')} ${message.price}
                                        </button>
                                    </>
                                )}

                                <div className={styles.wrapper}>
                                    <>
{/*                                         {!anyVideoPlaying &&
                                            !shouldHideBadges &&
                                            (showPriceBadge || showPremiumBadge) && (
                                                <div className={styles.info} style={{ cursor: 'pointer' }}>
                                                    {showPriceBadge ? (
                                                        <>
                                                            <spriteIcons.IconLockFill />
                                                            <p>${message.price}</p>
                                                        </>
                                                    ) : (
                                                        <p>Premium</p>
                                                    )}
                                                </div>
                                        )} */}
                                        {!anyVideoPlaying &&
                                            !shouldHideBadges &&
                                            showPriceBadge && (
                                                <div
                                                    className={styles.info}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <spriteIcons.IconLockFill />
                                                    <p>${message.price}</p>
                                                </div>
                                        )}
                                        {!anyVideoPlaying &&
                                            !shouldHideBadges &&
                                            totalDisplayMediaCount >= 2 && (
                                                <div className={styles.info}>
                                                    {displayVideoCount > 0 && (
                                                        <div
                                                            className={
                                                                styles.iconContainer
                                                            }
                                                        >
                                                            <spriteIcons.IconVideoOutlineCounter />
                                                            <div className={styles.count}>
                                                                <p>
                                                                    {displayVideoCount}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {displayPhotoCount > 0 && (
                                                        <div
                                                            className={
                                                                styles.iconContainer
                                                            }
                                                        >
                                                            <spriteIcons.IconImageOutlineCounter />
                                                            <div className={styles.count}>
                                                                <p>
                                                                    {displayPhotoCount}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                    </>
                                </div>
                            </>
                        </Swiper>
                    </div>

                    <MessageReactions
                        reactions={reactions}
                        containerClassName={styles.message_reactions_container}
                        reactionClassName={styles.message_reaction}
                    />
                </div>

                {!message.replied_message &&
                    !sending &&
                    !isWelcomeMessageRoute && (
                        <MessageOptions
                            message={message}
                            recieved={recieved}
                            hideOptionsForMedia={true}
                            addReaction={addReaction}
                        />
                    )}
            </div>
        )
    }

return (
    <div
        ref={messageContainerRef}
        className={`
            ${styles.message_container}
            ${styles.last_message_margin}
                ${recieved ? styles.message_container_recieved : ''}
                ${(prev_recieved ?? next_recieved) ? styles.message_grouped : ''}
                ${!next_recieved ? styles.message_grouped_first : ''}
                ${!sender ? styles.message_disabled : ''}
            `}
        >
            {recieved && !prev_recieved && (
                <div className={styles.sender_info}>
                    <div
                        className={styles.sender_avatar}
                        onClick={() => {
                            if (otherUserData?.deleted) {
                                addToast('error', t('userDeleted'))
                                return
                            }
                            if (userData?.role === 'fan') {
                                navigate(`/${otherUserData?.username}`)
                            } else {
                                modalData.addModal('', <UserDetails user={otherUserData} />, true)
                            }
                        }}
                    >
                        <img
                            src={sender?.avatar?.url || avatarPlaceHolder}
                            alt="avatar"
                        />
                    </div>
                    <div className={styles.sender_name}>
                        {sender?.name || sender?.display_name || t('deletedUser')}
                    </div>
                </div>
            )}

            {renderMediaNew()}
            {renderStoryReply()}
            {renderPollReply()}

            {message.gif_id && <GifLoader id={message.gif_id} />}

            <MessageOptionsDropdown
            isOpen={messageOptionsOpen}
            setIsOpen={setMessageOptionsOpen}
            recieved={recieved}
            hasMedia={
                (message?.photos?.length ?? 0) > 0 ||
                (message?.videos?.length ?? 0) > 0
            }
            messageId={String(message.id)}
            onDelete={() => {
                deleteMessage(message.id)
            }}
            onReport={() => {
                reportMessage(message.id)
            }}
            />

            {previewOpen && (
                <div className={styles.fullscreen_message_layer}>
                <FullscreenMessage
                    message={message}
                    media={
                        mergedMediaArray.filter(
                            (item: any) => item.type !== 'sound'
                        ) || []
                    }
                    closeFn={() => {
                        pause()
                        stopLoading()

                        setVideoPlaying([])
                        setPreviewOpen(false)
                    }}
                    purchaseMessage={purchaseMessage}
                    startingIndex={activeIndex}
                />
                </div>
            )}

            {(message.text || message.body || message.message) && (
                <div className={styles.message_text_wrapper}>
                    {(() => {
                        const isTextPresent = Boolean(
                            message?.text || message?.body || message?.message
                        )
                        const hasAnyMedia =
                            (mergedMediaArray?.length ?? 0) > 0 ||
                            Boolean(message?.gif_id) ||
                            message?.type === 'poll' ||
                            message?.type === 'story'
                        const isTextOnly = isTextPresent && !hasAnyMedia
                        const clicked =
                            message?.id != null && message?.id === messageId

                        const showOptions =
                            !message.replied_message &&
                            !sending &&
                            !welcomeMessage &&
                            (isTextOnly ? clicked : true)

                        if (!showOptions) return null

                        return (
                            <MessageOptions
                                message={message}
                                recieved={recieved}
                                addReaction={addReaction}
                                hideOptionsForMedia={!!checkLink(message.body)}
                            />
                        )
                    })()}

                    <div
                        className={`${
                            message.type === 'poll'
                                ? styles.message_poll_answer
                                : ''
                        }`}
                    >
                        <div
                            className={styles.message_text_container}
                            {...longPressHandlers}
                            onClickCapture={() =>
                                setMessageId?.(
                                    messageId === message.id ? null : message.id
                                )
                            }
                        >
                            {renderReply()}

                            <div
                                className={
                                    checkLink(message.body)
                                        ? ''
                                        : `${styles.message_text}`
                                }
                            >
                                {checkLink(message.body) ? (
                                <LinkPost message={message.body} />
                                ) : (
                                (() => {
                                    const plainText = String(
                                    message.text ??
                                        message.body ??
                                        message.message ??
                                        ''
                                    )

                                    return <span>{renderTextWithLinks(plainText)}</span>
                                })()
                                )}

                                <MessageReactions
                                    reactions={reactions}
                                    containerClassName={
                                        styles.message_reactions_container
                                    }
                                    reactionClassName={styles.message_reaction}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {!welcomeMessage &&
                (() => {
const clicked = message?.id != null && message?.id === messageId

const last =
    typeof isLastMessage !== 'undefined'
        ? isLastMessage
        : Boolean(message?.is_last_message)

const allow = !prev_recieved || clicked || last
if (!allow) return null

const isTextPresent = Boolean(
    message?.text || message?.body || message?.message
)

const hasAnyMedia =
    (mergedMediaArray?.length ?? 0) > 0 ||
    Boolean(message?.gif_id) ||
    message?.type === 'poll' ||
    message?.type === 'story'

const isTextOnly = isTextPresent && !hasAnyMedia

const shouldShowTimestamp = !isTextOnly || clicked || last
if (!shouldShowTimestamp) return null

const checkmarkColor = seen ? '#2F98FE' : '#B0B0B0'

return (
    <div
    ref={timestampRef}
        className={`${styles.message_time_container} ${
            !isOwner
                ? (mergedMediaArray?.length ?? 0) > 0
                    ? styles.otherUserUnderPost
                    : styles.otherUserText
                : ''
        }`}
    >
        <div
            className={styles.message_time}
            onClick={toggleTime}
            style={{ cursor: 'pointer' }}
            title="Click to toggle time format"
        >
            {formattedTime}
        </div>

        {!recieved && (
            <div className={styles.message_checkmarks}>
                <spriteIcons.IconCheckmarkSm color={checkmarkColor} />

                {!sending && (
                    <spriteIcons.IconCheckmarkSm color={checkmarkColor} />
                )}
            </div>
        )}
    </div>
)
                })()}
        </div>
    )
}

const StoryContainer = styled.div`
    width: 10rem;
    height: 17.7rem;
    margin-top: 0.5rem;
    margin-bottom: 0.3rem;
    position: relative;

    & > a {
        border-radius: 1.5rem;
    }
`
const Text = styled.span`
    color: ${colors.grey};
    margin-bottom: 0.4rem;
`

const PollContainer = styled.div`
    background-color: #e3e7eb;
    padding: 1.7rem;
    border-radius: 2rem;
    margin-top: -1.5rem;
    margin-left: 4rem;
    max-width: 27rem;
    width: 75%;
    transform: translateY(2.1rem);
`

const PollTitle = styled.div`
    font-weight: 600;
    font-size: 1.2rem;
    color: #a8bbcd;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.8rem;
`
const PollQuestion = styled.div`
    font-weight: 600;
    font-size: 1.2rem;
    color: #778797;
    margin-bottom: 2rem;

    font-family: Montserrat;
    font-size: 14px;
    font-style: italic;
    font-weight: 400;
    line-height: 21px;
    text-align: left;
`

const Placeholder = styled.div`
    position: relative;
    width: 21.5rem;
    height: 30rem;
    border-radius: 2rem;
    overflow: hidden;

    & > svg {
        position: absolute;
        z-index: 2;
        top: 45%;
        left: 50%;
        transform: translate(-50%, -50%);
    }
`
const PlaceholderBackground = styled.div`
    width: 100%;
    height: 100%;
    position: absolute;
    background: linear-gradient(140.46deg, #6b7e8d 14.38%, #2894ff 94.98%);
    opacity: 0.15;
    border-radius: 2rem;
`
const PaginationBullet = createGlobalStyle`
    .swiper-pagination {
        bottom: 1.9rem !important;
    }

    .message-swiper--locked .swiper-pagination {
        bottom: 1rem !important;
    }

    .swiper-pagination > .swiper-pagination-bullet-active {
        background-color: ${colors.white};
    }

    .swiper-horizontal > .swiper-pagination-bullets .swiper-pagination-bullet,
    .swiper-pagination-horizontal.swiper-pagination-bullets .swiper-pagination-bullet {
        margin: 0 4.4px !important;
    }
`
const ProgressBarWrapper = styled.div`
  padding: 0 0.1rem;
  position: absolute;
  bottom: 0;
  width: 100%;
  z-index: 6;
  cursor: pointer;
`
const Progress = styled.div`
  width: 100%;
  margin-bottom: 0;
  progress { width: 100%; }
`
const MostPlayed = styled.div<{ $compact?: boolean }>`
  padding: 0 0.1rem;
  position: absolute;
  bottom: ${({ $compact }) => ($compact ? '1rem' : '3.8rem')};
  z-index: 5;
  cursor: pointer;
  left: 0.7rem;
  width: 100%;
  -webkit-tap-highlight-color: transparent;
`
const GraphButton = styled.button`
  color: white;
  border: none;
  padding: 3px 3px;
  padding-left:.5rem;
  padding-right:0.5rem;
  border-radius: 0.6rem;
  cursor: pointer;
  z-index: 1000;
  background: linear-gradient(157.59deg, rgba(24,25,32,0.314) 0%, rgba(15,15,19,0.314) 100%);
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 1.2rem;
`
const GraphBar = styled.div`
  position: absolute;
  bottom: -3.5rem;
  left: -0.6rem;
  width: 100%;
  height: 70px;
  display: flex;
  align-items: flex-end;
  pointer-events: none;
  z-index: 999999;
  background: transparent;
  overflow: visible;
  &::after {
    content: '';
    position: absolute;
    left: 0; right: 0; bottom: -16px;
    height: 16px;
    background: rgba(0,0,0,0.40);
    pointer-events: none;
    z-index: 0;
  }
  svg { position: relative; z-index: 1; display: block; }
`


export default Message
